// Bellknop in de bovenbalk: meldingen van de OS aan of uit zetten op dit apparaat.
// Een melding (bv. een nieuwe lead) komt dan ook binnen als de app dicht is.
// Op een iPhone werkt dit alleen als de OS als app op het beginscherm staat.
import { useEffect, useState } from "react";
import { supabase } from "./supabase";
import { GELUIDEN, leesKeuze, bewaarKeuze, speel, ontgrendel } from "./leadGeluid";

const C = { bg: "#0B1528", border: "#1c2a44", text: "#e6ecf5", muted: "#8da0bd", green: "#34d399", yellow: "#f5b942" };

function sleutelNaarBytes(b64) {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

const ondersteund = () =>
  typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export default function MeldingenKnop({ user }) {
  const [open, setOpen] = useState(false);
  const [aan, setAan] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [bericht, setBericht] = useState("");
  const [geluidLead, setGeluidLead] = useState(leesKeuze("lead"));
  const [geluidInkomsten, setGeluidInkomsten] = useState(leesKeuze("inkomsten"));
  const ok = ondersteund();

  // Geluid voor een nieuwe lead als de OS open staat: de service worker meldt elke push aan de open pagina.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const eersteAanraking = () => ontgrendel();
    window.addEventListener("pointerdown", eersteAanraking, { once: true });
    window.addEventListener("keydown", eersteAanraking, { once: true });
    const opBericht = (e) => { const soort = e.data && e.data.type === "os-push" ? e.data.soort : null; if (soort === "lead" || soort === "inkomsten") speel(leesKeuze(soort)); };
    if ("serviceWorker" in navigator) navigator.serviceWorker.addEventListener("message", opBericht);
    return () => {
      window.removeEventListener("pointerdown", eersteAanraking);
      window.removeEventListener("keydown", eersteAanraking);
      if ("serviceWorker" in navigator) navigator.serviceWorker.removeEventListener("message", opBericht);
    };
  }, []);

  function kiesGeluid(soort, k) {
    if (soort === "lead") setGeluidLead(k); else setGeluidInkomsten(k);
    bewaarKeuze(soort, k);
    if (k !== "uit") speel(k);
  }

  useEffect(() => {
    if (!ok) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setAan(!!sub && Notification.permission === "granted");
      } catch { /* stil: de knop toont dan gewoon "uit" */ }
    })();
  }, [ok]);

  async function zetAan() {
    setBezig(true); setBericht("");
    try {
      const toestemming = await Notification.requestPermission();
      if (toestemming !== "granted") { setBericht("Meldingen zijn geblokkeerd. Zet ze aan in de instellingen van je browser of telefoon en probeer opnieuw."); return; }
      const { data: s, error: se } = await supabase.from("app_settings").select("value").eq("key", "push_vapid_publiek").maybeSingle();
      if (se || !s?.value) throw new Error("sleutel niet gevonden");
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: sleutelNaarBytes(s.value) });
      const j = sub.toJSON();
      const { error } = await supabase.from("push_abonnementen").upsert(
        { gebruiker: user?.email || null, endpoint: j.endpoint, p256dh: j.keys.p256dh, auth: j.keys.auth, apparaat: (navigator.userAgent || "").slice(0, 200) },
        { onConflict: "endpoint" });
      if (error) throw error;
      setAan(true); setBericht("Meldingen staan aan op dit apparaat.");
    } catch (e) {
      setBericht("Aanzetten lukte niet: " + (e?.message || e));
    } finally { setBezig(false); }
  }

  async function zetUit() {
    setBezig(true); setBericht("");
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await supabase.from("push_abonnementen").delete().eq("endpoint", sub.endpoint);
        await sub.unsubscribe();
      }
      setAan(false); setBericht("Meldingen staan uit op dit apparaat.");
    } catch (e) {
      setBericht("Uitzetten lukte niet: " + (e?.message || e));
    } finally { setBezig(false); }
  }

  async function test(soort) {
    setBezig(true); setBericht("");
    try {
      const body = soort === "inkomsten"
        ? { titel: "Testmelding: nieuwe bestelling € 49,95", tekst: "Zo klinkt en ziet een melding van inkomsten eruit.", soort: "inkomsten" }
        : { titel: "Testmelding: nieuwe lead", tekst: "Zo klinkt en ziet een melding van een nieuwe lead eruit.", soort: "lead" };
      const { data, error } = await supabase.functions.invoke("push-melding", { body });
      if (error) throw error;
      setBericht(data?.verstuurd > 0 ? "Testmelding verstuurd. Je ziet hem zo verschijnen." : "Er is geen apparaat aangemeld. Zet eerst meldingen aan.");
    } catch (e) {
      setBericht("Testmelding lukte niet: " + (e?.message || e));
    } finally { setBezig(false); }
  }

  const knop = { background: "transparent", border: "1px solid " + C.border, color: C.text, borderRadius: 6, padding: "8px 12px", fontSize: 12, cursor: "pointer", width: "100%", textAlign: "left" };

  return (
    <div style={{ position: "relative" }}>
      <button className="btn bg" style={{ fontSize: 14, padding: "5px 10px", position: "relative" }} onClick={() => setOpen((v) => !v)} title={aan ? "Meldingen staan aan" : "Meldingen staan uit"} aria-label="Meldingen">
        🔔
        <span style={{ position: "absolute", top: 3, right: 3, width: 7, height: 7, borderRadius: "50%", background: aan ? C.green : C.muted, border: "1px solid " + C.bg }} />
      </button>
      {open && <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 99 }} />}
      {open && (
        <div style={{ position: "fixed", right: 8, top: 56, background: "#101c33", border: "1px solid " + C.border, borderRadius: 8, padding: 14, zIndex: 100, width: "min(320px, calc(100vw - 16px))", maxHeight: "calc(100vh - 72px)", overflowY: "auto", boxSizing: "border-box", boxShadow: "0 8px 24px rgba(0,0,0,.4)" }}>
          <p style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, color: C.text }}>Meldingen</p>
          {!ok ? (
            <p style={{ fontSize: 12, lineHeight: 1.6, color: C.muted }}>
              Dit apparaat of deze browser ondersteunt geen meldingen. Op een iPhone: open de OS in Safari, tik op Deel en dan "Zet op beginscherm". Open de OS daarna vanaf je beginscherm en tik hier opnieuw op de bel.
            </p>
          ) : (
            <>
              <p style={{ fontSize: 12, lineHeight: 1.6, color: C.muted, marginBottom: 10 }}>
                {aan ? "Je krijgt een melding op dit apparaat bij elke nieuwe lead, ook als de app dicht is." : "Zet meldingen aan om bij elke nieuwe lead meteen een melding te krijgen, ook als de app dicht is."}
              </p>
              {aan ? (
                <div style={{ display: "grid", gap: 6 }}>
                  <button style={knop} disabled={bezig} onClick={() => test("lead")}>Stuur een testmelding van een lead</button>
                  <button style={knop} disabled={bezig} onClick={() => test("inkomsten")}>Stuur een testmelding van inkomsten</button>
                  <button style={knop} disabled={bezig} onClick={zetUit}>Meldingen uitzetten op dit apparaat</button>
                </div>
              ) : (
                <button style={{ ...knop, background: "#1d4ed8", borderColor: "#1d4ed8", fontWeight: 700 }} disabled={bezig} onClick={zetAan}>{bezig ? "Bezig..." : "Meldingen aanzetten"}</button>
              )}
            </>
          )}
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid " + C.border }}>
            {[["lead", "Geluid bij een nieuwe lead", geluidLead], ["inkomsten", "Geluid bij inkomsten (bestelling betaald)", geluidInkomsten]].map(([soort, label, waarde]) => (
              <div key={soort} style={{ marginBottom: 10 }}>
                <p style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, color: C.text }}>{label}</p>
                <div style={{ display: "flex", gap: 6 }}>
                  <select value={waarde} onChange={(e) => kiesGeluid(soort, e.target.value)} style={{ flex: 1, fontSize: 13, padding: "7px 8px", minWidth: 0 }}>
                    {Object.entries(GELUIDEN).map(([k, g]) => <option key={k} value={k}>{g.naam}</option>)}
                    <option value="uit">Geen geluid</option>
                  </select>
                  <button style={{ ...knop, width: "auto", flexShrink: 0 }} onClick={() => { ontgrendel(); speel(waarde); }} aria-label="Luister">▶</button>
                </div>
              </div>
            ))}
            <p style={{ fontSize: 11, lineHeight: 1.5, marginTop: 4, color: C.muted }}>Deze geluiden hoor je als de OS open staat. Als de app dicht is, bepaalt je telefoon of computer het geluid van de melding. Leads en inkomsten trillen wel anders (Android) en blijven op je scherm staan tot je ze wegveegt.</p>
          </div>
          {bericht && <p style={{ fontSize: 12, lineHeight: 1.5, marginTop: 10, color: C.yellow }}>{bericht}</p>}
        </div>
      )}
    </div>
  );
}
